import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/components/auth/AuthProvider';
import { useAccount } from '../components/auth/AccountContext';
import { AccountUser } from '../entities/AccountUser';

export type UserRole = 'admin' | 'member' | 'planner' | 'normal';

interface RoleContextType {
  currentUserRole: UserRole | null;
  isLoading: boolean;
  error: string | null;
  // Verificações de permissão
  canAccessPage: (page: string) => boolean;
  canEditRecords: () => boolean;
  canEditAccount: () => boolean;
  canManageAccount: () => boolean;
  canInviteUsers: () => boolean;
  canManageUsers: () => boolean;
  canManagePlans: () => boolean;
  refreshRole: () => Promise<void>;
}

const RoleContext = createContext<RoleContextType | undefined>(undefined);

export function RoleProvider({ children }: { children: React.ReactNode }) {
  // Usar hooks diretamente - se não estiverem disponíveis, o erro será tratado pelo ErrorBoundary
  const { user: authUser, loading: authLoading } = useAuth();
  const { currentAccountId, isLoading: accountLoading } = useAccount();

  const [currentUserRole, setCurrentUserRole] = useState<UserRole | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Função para carregar o role do usuário atual
  const loadUserRole = useCallback(async () => {
    // Se ainda está carregando auth ou account, aguardar
    if (authLoading || accountLoading) {
      setIsLoading(true);
      return;
    }

    if (!authUser || !currentAccountId) {
      setCurrentUserRole(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Timeout de segurança para evitar loading infinito
      const timeoutId = setTimeout(() => {
        console.warn('⚠️ [RoleProvider] Loading timeout - definindo role como null');
        setCurrentUserRole(null);
        setIsLoading(false);
        setError('Timeout ao carregar permissões');
      }, 5000); // 5 segundos

      const userAccountLinks = await AccountUser.filter({ 
        user_id: authUser.id, 
        account_id: currentAccountId 
      });

      clearTimeout(timeoutId);

      if (userAccountLinks.length > 0) {
        setCurrentUserRole(userAccountLinks[0].role as UserRole);
      } else {
        setCurrentUserRole(null);
      }
    } catch (err) {
      console.error('Erro ao carregar role do usuário:', err);
      setError('Erro ao carregar permissões do usuário');
      setCurrentUserRole(null);
    } finally {
      setIsLoading(false);
    }
  }, [authUser, currentAccountId, authLoading, accountLoading]);

  // Recarregar role quando usuário ou conta mudar
  useEffect(() => {
    loadUserRole();
  }, [loadUserRole]);

  // Função para refresh manual do role
  const refreshRole = useCallback(async () => {
    await loadUserRole();
  }, [loadUserRole]);

  // Verificações de permissão baseadas no role

  // Verificar se pode acessar uma página específica
  const canAccessPage = useCallback((page: string) => {
    if (!currentUserRole) return false;

    // Admin pode acessar tudo
    if (currentUserRole === 'admin') return true;

    // Planejador só pode acessar planos e análise
    if (currentUserRole === 'planner') {
      const allowedPages = ['/planning', '/dashboard'];
      return allowedPages.includes(page);
    }

    // Outros roles podem acessar tudo exceto configurações avançadas
    return true;
  }, [currentUserRole]);

  // Verificar se pode editar registros existentes
  const canEditRecords = useCallback(() => {
    if (!currentUserRole) return false;
    
    // Planejador não pode editar registros existentes
    if (currentUserRole === 'planner') return false;
    
    // Admin e outros podem editar
    return true;
  }, [currentUserRole]);

  // Verificar se pode editar dados da conta
  const canEditAccount = useCallback(() => {
    if (!currentUserRole) return false;
    
    // Apenas admin pode editar dados da conta
    return currentUserRole === 'admin';
  }, [currentUserRole]);

  // Verificar se pode convidar usuários
  const canInviteUsers = useCallback(() => {
    if (!currentUserRole) return false;
    
    // Planejador não pode convidar usuários
    if (currentUserRole === 'planner') return false;
    
    // Admin pode convidar
    return currentUserRole === 'admin';
  }, [currentUserRole]);

  // Verificar se pode gerenciar planos
  const canManagePlans = useCallback(() => {
    if (!currentUserRole) return false;
    
    // Admin e planejador podem gerenciar planos
    return currentUserRole === 'admin' || currentUserRole === 'planner';
  }, [currentUserRole]);

  // Alias para canEditAccount (compatibilidade)
  const canManageAccount = useCallback(() => {
    return canEditAccount();
  }, [canEditAccount]);

  // Alias para canInviteUsers (compatibilidade)
  const canManageUsers = useCallback(() => {
    return canInviteUsers();
  }, [canInviteUsers]);

  const value: RoleContextType = {
    currentUserRole,
    isLoading,
    error,
    canAccessPage,
    canEditRecords,
    canEditAccount,
    canManageAccount,
    canInviteUsers,
    canManageUsers,
    canManagePlans,
    refreshRole,
  };

  return (
    <RoleContext.Provider value={value}>
      {children}
    </RoleContext.Provider>
  );
}

export function useRole() {
  const context = useContext(RoleContext);
  if (context === undefined) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
}

// Hook para verificar se é um role específico
export function useIsRole(role: UserRole) {
  const { currentUserRole } = useRole();
  return currentUserRole === role;
}

// Hook para verificar se é planejador
export function useIsPlanner() {
  return useIsRole('planner');
}

// Hook para verificar se é admin
export function useIsAdmin() {
  return useIsRole('admin');
}
